import { Icon, Skeleton, Typography } from "@/components/atoms";
import { DocumentList } from "@/components/molecules";
import { useOperator } from "@/hooks";
import { RequirePermission } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { useGetOtherLinksQuery } from "@/redux/apis/config/linksAndDocuments";
import { useAppSelector } from "@/redux/hooks";
import { documentsSelector } from "@/redux/slices/documents/selectors";
import ComplaintsProtocolPDF from "@/assets/templates/complaints-protocol-provider-and-driver-guidance.pdf";
import EnterpriseAddendumPDF from "@/assets/templates/enterprise-custom-configuration-addendum.pdf";
import TermsAndConditionsPDF from "@/assets/templates/terms-and-conditions.pdf";
import { TermsAndConditions } from "./TermsAndConditions/TermsAndConditions";

// Provider codes that have access to Enterprise Addendum
const ENTERPRISE_ADDENDUM_PROVIDER_CODES = ["COD", "ENCORE", "ROYALE", "LIMO-AUSTRALIA"];

export const Documents = () => {
  const { providerCode } = useOperator();
  const { isFetching } = useGetOtherLinksQuery();
  const { otherLinks } = useAppSelector(documentsSelector);
  const customLinks = ["client_service_agreement", "driver_service_agreement"];
  const filteredOtherLinks = otherLinks.filter((link) => !customLinks.includes(link.document.slug));
  const driverServiceAgreement = otherLinks.find((link) => link.document.slug === "driver_service_agreement");
  const clientServiceAgreement = otherLinks.find((link) => link.document.slug === "client_service_agreement");

  // Check if operator has access to Enterprise Addendum (case-insensitive)
  const hasEnterpriseAddendumAccess = ENTERPRISE_ADDENDUM_PROVIDER_CODES.map((code) => code.toUpperCase()).includes(
    providerCode.toUpperCase()
  );

  if (isFetching) return <Skeleton />;

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <Typography variant="h3">Documents</Typography>
        <DocumentList>
          <DocumentList.Item
            link={TermsAndConditionsPDF}
            title="RideMinder Platform Usage Subscription Agreement"
            icon={<Icon name="Book" size="sm" />}
          />
          {hasEnterpriseAddendumAccess && (
            <DocumentList.Item
              link={EnterpriseAddendumPDF}
              title="RideMinder Enterprise Custom Configuration Addendum"
              icon={<Icon name="DocumentText" size="sm" />}
            />
          )}
          <DocumentList.Item
            link={ComplaintsProtocolPDF}
            title="RideMinder Complaints Protocol - Provider & Driver Guidance"
            icon={<Icon name="DocumentText" size="sm" />}
          />
          {filteredOtherLinks.map((i) => (
            <DocumentList.Item
              key={i.id}
              link={i.document.link}
              title={i.document.name}
              subtext={i.document.description}
              icon={i.icon !== "document" ? <Icon name={i.icon} size="sm" isCustom /> : <Icon name="Book" size="sm" />}
            />
          ))}
        </DocumentList>
      </div>
      <RequirePermission permission={EPermission.CustomServiceAgreementManage}>
        <div className="flex flex-col gap-6">
          <Typography variant="h3">Custom Service Agreements</Typography>
          <DocumentList>
            <DocumentList.CustomItem
              id={driverServiceAgreement?.id}
              document={driverServiceAgreement?.document}
              title="Driver Service Agreement"
              documentType="driver_service_agreement"
              icon={<Icon name="Book" size="sm" />}
            />
            <DocumentList.CustomItem
              id={clientServiceAgreement?.id}
              document={clientServiceAgreement?.document}
              title="Client Service Agreement"
              documentType="client_service_agreement"
              icon={<Icon name="Book" size="sm" />}
            />
          </DocumentList>
        </div>
      </RequirePermission>
    </div>
  );
};

Documents.TermsAndConditions = TermsAndConditions;
